/**
 * The Scada.Ink Cloud subscription catalogue.
 *
 * One list read by both the marketing pricing page and the cloud billing
 * service, so the price a customer sees is the price they are charged for and
 * the allowance the portal shows is the allowance the relay enforces.
 *
 * Prices are PLACEHOLDERS pending a pricing decision. Stripe price ids are not
 * kept here — the cloud maps `stripePriceEnv` to an environment variable, so a
 * price change in Stripe does not need a code change, and a plan whose
 * variable is unset falls back to invoice billing rather than failing.
 */

export type CloudPlanCategory = 'ai' | 'archive' | 'remote' | 'notifications' | 'bundle';

/** Whether a plan is bought once for the account or once per appliance. */
export type BillingUnit = 'account' | 'appliance';

export interface PlanLimits {
  /** Smart Assist tag classification + observation analyses per month. */
  aiRequestsPerMonth?: number;
  /** AI custom-page designs per month. */
  aiPageDesignsPerMonth?: number;
  /** Deep (multi-stage) observation analysis is available. */
  aiDeepAnalysis?: boolean;
  archiveRetentionDays?: number;
  /** Historian points uploaded per appliance per month. */
  archivePointsPerMonth?: number;
  /** Named remote viewers per appliance. `null` = unlimited. */
  remoteViewers?: number | null;
  smsPerMonth?: number;
  voiceMinutesPerMonth?: number;
}

export interface CloudPlan {
  id: CloudPlanId;
  category: CloudPlanCategory;
  name: string;
  tagline: string;
  priceMonthlyUsd: number;
  unit: BillingUnit;
  /** Drawn as the recommended option in its category. */
  highlight?: boolean;
  features: string[];
  limits: PlanLimits;
  /** For bundles: the plans whose entitlements this one grants. */
  includes?: CloudPlanId[];
  /** Environment variable holding this plan's Stripe price id. */
  stripePriceEnv: string;
}

export type CloudPlanId =
  | 'ai-assist'
  | 'ai-assist-pro'
  | 'archive-standard'
  | 'archive-longterm'
  | 'remote-view'
  | 'remote-view-plus'
  | 'notify-standard'
  | 'notify-pro'
  | 'cloud-complete'
  | 'cloud-complete-pro';

export const CLOUD_PLAN_CATEGORIES: Array<{ id: CloudPlanCategory; name: string; summary: string }> = [
  {
    id: 'ai',
    name: 'Smart Assist AI',
    summary: 'AI tag classification and page design, plus observation analysis on Pro.',
  },
  {
    id: 'archive',
    name: 'Cloud Data Archive',
    summary: 'Off-site historian archive with long retention and export.',
  },
  {
    id: 'remote',
    name: 'Remote Viewing',
    summary: 'Dashboards, custom pages and reports from anywhere.',
  },
  {
    id: 'notifications',
    name: 'SMS & Voice Alerts',
    summary: 'Twilio-powered text and phone-call alarm notifications.',
  },
  {
    id: 'bundle',
    name: 'Cloud Complete',
    summary: 'Every cloud service for one appliance, at a bundle price — standard or Pro.',
  },
];

export const CLOUD_PLANS: CloudPlan[] = [
  {
    id: 'ai-assist',
    category: 'ai',
    name: 'Smart Assist',
    tagline: 'AI setup help for a single site',
    priceMonthlyUsd: 49,
    unit: 'account',
    features: [
      'AI tag classification for a connected device',
      'Suggested tag roles and dashboards from scanned devices',
      'AI custom page designer',
      'Works on air-gapped appliances (relayed through your browser)',
    ],
    limits: { aiRequestsPerMonth: 300, aiPageDesignsPerMonth: 20, aiDeepAnalysis: false },
    stripePriceEnv: 'STRIPE_PRICE_AI_ASSIST',
  },
  {
    id: 'ai-assist-pro',
    category: 'ai',
    name: 'Smart Assist Pro',
    tagline: 'For integrators commissioning many machines',
    priceMonthlyUsd: 149,
    unit: 'account',
    highlight: true,
    features: [
      'Everything in Smart Assist',
      'Observation analysis: alarm limits & deadbands from real data',
      '5× the analysis allowance',
      'Priority model capacity',
    ],
    limits: { aiRequestsPerMonth: 1500, aiPageDesignsPerMonth: 100, aiDeepAnalysis: true },
    stripePriceEnv: 'STRIPE_PRICE_AI_ASSIST_PRO',
  },
  {
    id: 'archive-standard',
    category: 'archive',
    name: 'Archive',
    tagline: '13 months of off-site history',
    priceMonthlyUsd: 39,
    unit: 'appliance',
    highlight: true,
    features: [
      'Encrypted off-site copy of historian data',
      '13-month retention',
      'CSV export of any tag and time range',
      'Store-and-forward — uploads resume after outages',
    ],
    limits: { archiveRetentionDays: 395, archivePointsPerMonth: 50_000_000 },
    stripePriceEnv: 'STRIPE_PRICE_ARCHIVE_STANDARD',
  },
  {
    id: 'archive-longterm',
    category: 'archive',
    name: 'Archive Long-Term',
    tagline: 'Seven-year retention for regulated plants',
    priceMonthlyUsd: 99,
    unit: 'appliance',
    features: [
      'Everything in Archive',
      '7-year retention',
      '5× the monthly point allowance',
      'Restore archived history back to an appliance',
    ],
    limits: { archiveRetentionDays: 2555, archivePointsPerMonth: 250_000_000 },
    stripePriceEnv: 'STRIPE_PRICE_ARCHIVE_LONGTERM',
  },
  {
    id: 'remote-view',
    category: 'remote',
    name: 'Remote View',
    tagline: 'See the floor from the office or the road',
    priceMonthlyUsd: 29,
    unit: 'appliance',
    highlight: true,
    features: [
      'Read-only dashboards, custom pages and reports',
      'Report download links for viewers',
      'Up to 10 named viewers',
      'Viewer access managed from the portal',
      'No inbound firewall ports opened',
    ],
    limits: { remoteViewers: 10 },
    stripePriceEnv: 'STRIPE_PRICE_REMOTE_VIEW',
  },
  {
    id: 'remote-view-plus',
    category: 'remote',
    name: 'Remote View Plus',
    tagline: 'Plant-wide visibility for the whole company',
    priceMonthlyUsd: 79,
    unit: 'appliance',
    features: ['Everything in Remote View', 'Unlimited named viewers'],
    limits: { remoteViewers: null },
    stripePriceEnv: 'STRIPE_PRICE_REMOTE_VIEW_PLUS',
  },
  {
    id: 'notify-standard',
    category: 'notifications',
    name: 'Alerts',
    tagline: 'SMS and voice alarms for a site',
    priceMonthlyUsd: 25,
    unit: 'account',
    highlight: true,
    features: [
      '500 SMS messages per month',
      '100 voice-call minutes per month',
      'Managed Twilio number, or bring your own Twilio account and caller ID',
      'Recipient allow-list and monthly caps',
    ],
    limits: { smsPerMonth: 500, voiceMinutesPerMonth: 100 },
    stripePriceEnv: 'STRIPE_PRICE_NOTIFY_STANDARD',
  },
  {
    id: 'notify-pro',
    category: 'notifications',
    name: 'Alerts Pro',
    tagline: 'High-volume escalation for multi-shift plants',
    priceMonthlyUsd: 79,
    unit: 'account',
    features: [
      '2,500 SMS messages per month',
      '500 voice-call minutes per month',
      'Managed Twilio number, or bring your own Twilio account and caller ID',
      'Full delivery log with per-appliance attribution',
    ],
    limits: { smsPerMonth: 2500, voiceMinutesPerMonth: 500 },
    stripePriceEnv: 'STRIPE_PRICE_NOTIFY_PRO',
  },
  {
    id: 'cloud-complete',
    category: 'bundle',
    name: 'Cloud Complete',
    tagline: 'Every cloud service, one price per appliance',
    priceMonthlyUsd: 119,
    unit: 'appliance',
    highlight: true,
    features: [
      'Smart Assist AI',
      'Cloud Data Archive (13-month)',
      'Remote View (10 viewers)',
      'SMS & Voice Alerts',
      'Fleet health monitoring',
    ],
    limits: {},
    includes: ['ai-assist', 'archive-standard', 'remote-view', 'notify-standard'],
    stripePriceEnv: 'STRIPE_PRICE_CLOUD_COMPLETE',
  },
  {
    id: 'cloud-complete-pro',
    category: 'bundle',
    name: 'Cloud Complete Pro',
    tagline: 'Every Pro cloud service, one price per appliance',
    priceMonthlyUsd: 349,
    unit: 'appliance',
    features: [
      'Smart Assist Pro AI with deep analysis',
      'Cloud Data Archive Long-Term (7-year)',
      'Remote View Plus (unlimited viewers)',
      'Alerts Pro SMS & Voice (higher monthly volumes)',
      'Fleet health monitoring',
    ],
    limits: {},
    includes: ['ai-assist-pro', 'archive-longterm', 'remote-view-plus', 'notify-pro'],
    stripePriceEnv: 'STRIPE_PRICE_CLOUD_COMPLETE_PRO',
  },
];

export function findCloudPlan(id: string): CloudPlan | undefined {
  return CLOUD_PLANS.find((p) => p.id === id);
}

/** The sum of the plans a bundle includes, bought separately. */
export function bundleListPrice(plan: CloudPlan): number {
  return (plan.includes ?? []).reduce((sum, id) => sum + (findCloudPlan(id)?.priceMonthlyUsd ?? 0), 0);
}
